import { format } from "date-fns";
import {
  BID_STATUS,
  FULL_BOARD_INCREMENT_CENTS,
  LEDGER_TYPE,
  MIN_BID_CENTS,
  SLOT_COUNT,
} from "./zipSlotAdsConstants";

export function resolveInstitutionId({
  userData,
  tokenClaims,
  organizationId,
} = {}) {
  const candidates = [
    userData?.activeInstitutionId,
    userData?.hierarchyInstitutionId,
    userData?.institutionId,
    organizationId,
    userData?.organizationId,
    tokenClaims?.institutionId,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

/**
 * Defense-in-depth strip. The server already removes competitor institutionIds
 * via getZipSlotRanksForDisplay (advertiser path); this never copies
 * institutionId into UI state.
 */
export function sanitizeRankSlots(slots, ownInstitutionId) {
  const ownId = typeof ownInstitutionId === "string" ? ownInstitutionId : "";
  return (Array.isArray(slots) ? slots : [])
    .map((slot) => {
      const rank = Number(slot?.rank);
      if (!Number.isInteger(rank) || rank < 1 || rank > SLOT_COUNT) return null;
      const isOwn =
        typeof slot?.isOwn === "boolean"
          ? slot.isOwn
          : Boolean(ownId) && slot?.institutionId === ownId;
      return {
        rank,
        bidPerClickCents: Number(slot?.bidPerClickCents) || 0,
        isOwn,
      };
    })
    .filter(Boolean);
}

export function buildSlotBoard(sanitizedSlots) {
  const byRank = new Map();
  (sanitizedSlots || []).forEach((slot) => {
    byRank.set(slot.rank, { ...slot, filled: true });
  });
  const board = [];
  for (let rank = 1; rank <= SLOT_COUNT; rank += 1) {
    board.push(
      byRank.get(rank) || {
        rank,
        bidPerClickCents: null,
        isOwn: false,
        filled: false,
      },
    );
  }
  return board;
}

export function minBidCentsToPlace({ board, alreadyOnBoard }) {
  const filledCount = (board || []).filter((slot) => slot.filled).length;
  if (filledCount >= SLOT_COUNT && !alreadyOnBoard) {
    const seventh = (board || []).find((slot) => slot.rank === SLOT_COUNT);
    return (Number(seventh?.bidPerClickCents) || 0) + FULL_BOARD_INCREMENT_CENTS;
  }
  return MIN_BID_CENTS;
}

export function dollarsToCents(dollars) {
  if (dollars === "" || dollars == null) return null;
  const n = Number(dollars);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

export function centsToDollarString(cents) {
  if (cents == null || !Number.isFinite(Number(cents))) return "";
  return (Number(cents) / 100).toFixed(2);
}

export function formatCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n / 100);
}

export function isLowBalance(balanceCents, highestActiveBidCents) {
  const balance = Number(balanceCents) || 0;
  const highest = Number(highestActiveBidCents) || 0;
  if (highest > 0) return balance < highest;
  return false;
}

export function ledgerTypeLabel(type) {
  switch (type) {
    case LEDGER_TYPE.TOPUP:
      return "Top-up";
    case LEDGER_TYPE.CLICK_DEBIT:
      return "Click debit";
    case LEDGER_TYPE.REFUND:
      return "Refund";
    case LEDGER_TYPE.AUTO_RECHARGE:
      return "Auto-recharge";
    default:
      return type ? String(type) : "Other";
  }
}

export function firestoreDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatLedgerTimestamp(value) {
  const d = firestoreDate(value);
  return d ? format(d, "MMM d, yyyy h:mm a") : "—";
}

export function aggregateClickMetrics(entries) {
  const byZipMap = new Map();
  let clickCount = 0;
  let clickSpendCents = 0;

  (entries || []).forEach((entry) => {
    if (entry?.type !== LEDGER_TYPE.CLICK_DEBIT) return;
    clickCount += 1;
    const spend = Math.abs(Number(entry.amountCents) || 0);
    clickSpendCents += spend;
    const zip = entry.relatedZip ? String(entry.relatedZip) : "unknown";
    const prev = byZipMap.get(zip) || {
      zip,
      clickCount: 0,
      clickSpendCents: 0,
    };
    prev.clickCount += 1;
    prev.clickSpendCents += spend;
    byZipMap.set(zip, prev);
  });

  return {
    clickCount,
    clickSpendCents,
    byZip: Array.from(byZipMap.values()),
  };
}

export function uniqueRelatedZips(entries) {
  const zips = new Set();
  (entries || []).forEach((entry) => {
    const zip = String(entry?.relatedZip || "").trim();
    if (/^\d{5}$/.test(zip)) zips.add(zip);
  });
  return Array.from(zips);
}

export function ownBidIsOnBoard(ownBid) {
  return Boolean(ownBid) && ownBid.status === BID_STATUS.ACTIVE;
}

export function callableErrorMessage(err, fallback) {
  let message = err?.message || fallback || "Something went wrong.";
  message = message
    .replace(/^FirebaseError:\s*/i, "")
    .replace(/^Firebase:\s*/i, "");
  return message;
}

export function isValidZip(value) {
  return /^\d{5}$/.test(String(value || "").trim());
}
